import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { BackIcon } from '../components/chrome/BackIcon.js';
import { PersonAvatar } from '../components/chrome/PersonAvatar.js';
import { Skeleton } from '../components/ui/Skeleton.js';
import { dismissInvite, INVITES_KEY, listInvites, recordInvite } from '../lib/api/invites.js';
import { characterImage, DEFAULT_CHARACTER } from '../lib/characters.js';
import { inviteLink, inviteText } from '../lib/invite.js';
import { ensureMatrixClient } from '../lib/matrix/client.js';
import { startDirectMessage } from '../lib/matrix/rooms.js';
import { hapticSuccess, hapticTap, hapticWarn, shareText } from '../lib/native.js';
import { useSession } from '../lib/session.js';
import { useProfile } from '../lib/useProfile.js';
import './convite-conversa.css';

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');

/**
 * A conversa aberta com quem foi convidado e ainda não entrou: um cartão amigável
 * ("a Ana ainda não aceitou o convite") com Reenviar. Quando a pessoa entra, o
 * mesmo lugar vira "a Ana entrou" e um toque começa a conversa protegida.
 */
export function ConviteConversaPage() {
  const { contactId = '' } = useParams<{ contactId: string }>();
  const [, navigate] = useLocation();
  const session = useSession();
  const profile = useProfile();
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'resend' | 'start' | 'remove' | null>(null);
  const invitesQ = useQuery({ queryKey: INVITES_KEY, queryFn: listInvites, staleTime: 10_000 });
  const invite = invitesQ.data?.find((item) => item.contactId === contactId) ?? null;
  const first = (invite?.name ?? '').trim().split(/\s+/)[0] || 'Essa pessoa';
  const art = characterImage(profile.data?.characterKey ?? DEFAULT_CHARACTER, 'simpatico');

  async function resend() {
    const username = session?.user.username;
    if (!username || !invite) return;
    setBusy('resend');
    setNote('');
    try {
      const text = inviteText(username);
      const link = inviteLink(username);
      if (await shareText(text, link)) {
        await recordInvite(invite.contactId);
        hapticSuccess();
        setNote('Convite reenviado.');
      } else {
        await navigator.clipboard.writeText(`${text}\n${link}`);
        await recordInvite(invite.contactId);
        setNote('Convite copiado. É só colar na conversa com a pessoa.');
      }
      await qc.invalidateQueries({ queryKey: INVITES_KEY });
    } catch {
      setNote('Não consegui abrir o compartilhamento agora.');
      void hapticWarn();
    } finally {
      setBusy(null);
    }
  }

  async function start() {
    if (!invite?.joinedUsername) return;
    setBusy('start');
    try {
      const client = await ensureMatrixClient();
      if (!client) throw new Error('mensageiro indisponível');
      const roomId = await startDirectMessage(client, `@${invite.joinedUsername}:${client.getDomain()}`);
      await dismissInvite(invite.contactId).catch(() => undefined);
      await qc.invalidateQueries({ queryKey: INVITES_KEY });
      hapticSuccess();
      navigate(`/conversas/${encodeURIComponent(roomId)}`, { replace: true });
    } catch {
      setNote('Não consegui abrir a conversa agora. Tente de novo.');
      void hapticWarn();
      setBusy(null);
    }
  }

  async function remove() {
    if (!invite) return;
    setBusy('remove');
    hapticTap();
    await dismissInvite(invite.contactId).catch(() => undefined);
    await qc.invalidateQueries({ queryKey: INVITES_KEY });
    navigate('/conversas', { replace: true });
  }

  return (
    <div className="cvi-page">
      <header className="cvi-head">
        <button type="button" className="cvi-back" aria-label="Voltar" onClick={() => history.back()}>
          <BackIcon />
        </button>
        <PersonAvatar name={invite?.name ?? first} className="cvi-avatar" />
        <span className="cvi-who">
          <b>{invite?.name ?? 'Convite'}</b>
          <small>{invite?.joinedUsername ? `@${invite.joinedUsername}` : 'Convite enviado'}</small>
        </span>
      </header>
      <main className="cvi-body">
        {invitesQ.isLoading ? (
          <Skeleton variant="rows" label="Abrindo o convite" />
        ) : !invite ? (
          <div className="cvi-card">
            <h2>Esse convite não está mais aqui</h2>
            <p>Ele pode ter sido tirado da lista. Mande de novo pela página do contato.</p>
            <button type="button" className="cvi-btn" onClick={() => navigate('/conversas')}>
              Voltar às conversas
            </button>
          </div>
        ) : invite.joinedUsername ? (
          <div className="cvi-card cvi-joined" role="status">
            <img src={art} alt="" className="cvi-art" />
            <span className="cvi-kicker">Boa notícia</span>
            <h2>{first} entrou no Secretário</h2>
            <p>
              A conversa de vocês nasce protegida de ponta a ponta. Quer mandar um oi agora?
            </p>
            <button type="button" className="cvi-btn solid" disabled={busy === 'start'} onClick={() => void start()}>
              {busy === 'start' ? 'Abrindo…' : `Conversar com ${first}`}
            </button>
          </div>
        ) : (
          <div className="cvi-card" role="status">
            <img src={art} alt="" className="cvi-art" />
            <span className="cvi-kicker">Esperando {first}</span>
            <h2>{first} ainda não aceitou o seu convite</h2>
            <p>
              Mandei em {day(invite.invitedAt)}
              {invite.sentCount > 1 ? `, e de novo em ${day(invite.lastSentAt)}` : ''}. Assim que{' '}
              {first} entrar, eu aviso e a conversa de vocês começa aqui, protegida.
            </p>
            <div className="cvi-acts">
              <button type="button" className="cvi-btn solid" disabled={busy === 'resend'} onClick={() => void resend()}>
                {busy === 'resend' ? 'Abrindo…' : 'Reenviar convite'}
              </button>
              <button type="button" className="cvi-btn" onClick={() => navigate(`/contatos/${invite.contactId}`)}>
                Ver contato
              </button>
            </div>
            <button type="button" className="cvi-link" disabled={busy === 'remove'} onClick={() => void remove()}>
              Tirar da lista de conversas
            </button>
          </div>
        )}
        {note && (
          <p className="cvi-note" role="status">
            {note}
          </p>
        )}
      </main>
    </div>
  );
}
